import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NumberSeriesItem,
  SaveNumberSeriesBulkInput,
  SaveNumberSeriesInput,
} from '@tiles-erp/shared-types';
import { apiFetch } from '../../lib/api-client';

const KEY = 'number-series';

/**
 * Every document type for a branch, configured or not.
 *
 * The list is generated by the API rather than stored, so an unconfigured document shows
 * its built-in default instead of going missing.
 */
export function useNumberSeries(branchId: string | null) {
  return useQuery({
    queryKey: [KEY, branchId ?? 'company'],
    queryFn: () =>
      apiFetch<NumberSeriesItem[]>(
        `/number-series${branchId ? `?branchId=${branchId}` : ''}`,
      ),
  });
}

export function useSaveNumberSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveNumberSeriesInput) =>
      apiFetch<NumberSeriesItem[]>('/number-series', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

/** Saves several series in one go, so a branch can be set up in a single pass. */
export function useSaveNumberSeriesBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveNumberSeriesBulkInput) =>
      apiFetch<NumberSeriesItem[]>('/number-series/bulk', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}
